import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Loader2, Eye, EyeOff, Mail, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { BackButton } from '../components/BackButton';

const API = process.env.REACT_APP_BACKEND_URL;

// Resolve whitelabel branding for the current host (no auth needed).
// Cached in localStorage (survives tab closes) with a 24h TTL so returning
// visitors on whitelabel domains never see the "Konnect" branding flash.
const useHostBrand = () => {
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const cacheKey = `__wl_brand:${host}`;

  // Read cache SYNCHRONOUSLY during first render so the initial paint is
  // already whitelabel-correct. No flash even on cold sessionStorage.
  const [brand, setBrand] = useState(() => {
    try {
      const raw = localStorage.getItem(cacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // 24h TTL — long enough to eliminate flash, short enough for updates
      if (Date.now() - (parsed.__cachedAt || 0) < 86400000) return parsed.brand;
    } catch { /* ignore */ }
    return null;
  });
  const [resolved, setResolved] = useState(() => !!brand);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/whitelabel/public/by-host`, { params: { host } })
      .then((r) => {
        if (cancelled) return;
        if (r.data?.matched) {
          setBrand(r.data.brand);
          try {
            localStorage.setItem(cacheKey, JSON.stringify({
              brand: r.data.brand,
              __cachedAt: Date.now(),
            }));
          } catch { /* storage full — non-fatal */ }
        } else {
          // Explicit non-match → clear stale cache so a re-mapped domain
          // stops flashing the wrong brand.
          try { localStorage.removeItem(cacheKey); } catch { /* ignore */ }
          setBrand(null);
        }
      })
      .catch(() => { /* silent — fall back to Konnect default */ })
      .finally(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, []);

  return { brand, resolved };
};

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // Backend reachability: 'checking' | 'ready' | 'warming'
  // Users can still type + submit while checking/warming, but we show a
  // subtle banner so they know why the first attempt may take a bit longer.
  const [backendState, setBackendState] = useState('checking');
  // Passwordless-by-default — but remember if the user chose password last time
  // so returning users don't have to click "Use password instead" every visit.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('konnect_login_mode') === 'password' ? 'password' : 'magic'; }
    catch { return 'magic'; }
  });
  const [magicSent, setMagicSent] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const { brand, resolved: brandResolved } = useHostBrand();

  // Pre-flight health ping on mount — tells us if the backend is warm (K8s
  // pod up, Passenger process alive) or cold-starting. Prevents the classic
  // "click Sign In → wait forever → scary error" UX. Auth0/Firebase do the
  // same thing to short-circuit user frustration on cold containers.
  useEffect(() => {
    let cancelled = false;
    const startedAt = Date.now();
    // Fast timeout on the first ping — if server is warm it responds in <500ms.
    axios.get(`${API}/api/health`, { timeout: 4000 })
      .then(() => {
        if (!cancelled) setBackendState('ready');
      })
      .catch(() => {
        // Cold/warming — kick off a background poll that flips the state to
        // 'ready' the moment the server responds. Meanwhile the user can
        // still type; when they hit Sign In the retry logic in AuthContext
        // will patiently wait it out.
        if (!cancelled) setBackendState('warming');
        const poll = setInterval(async () => {
          if (cancelled || Date.now() - startedAt > 60000) {
            clearInterval(poll);
            return;
          }
          try {
            await axios.get(`${API}/api/health`, { timeout: 5000 });
            if (!cancelled) setBackendState('ready');
            clearInterval(poll);
          } catch { /* keep polling */ }
        }, 3000);
      });
    return () => { cancelled = true; };
  }, []);

  // Magic-link bypass: if URL has ?mlt=<short-token>, exchange it for a real
  // session via /api/auth/consume-magic-link. Falls back to the old ?token=
  // path for any in-flight links from the previous JWT-in-URL scheme.
  //
  // CRITICAL (2026-06-23): we must HARD-CLEAR any prior session BEFORE the
  // exchange. Previously, an already-logged-in user clicking a magic link
  // could end up landing in the OLD user's dashboard because the previous
  // Authorization header / localStorage tokens lingered and the AuthContext
  // never re-checked. Now we wipe localStorage + axios defaults + (best-
  // effort) service-worker caches, run the exchange anonymously, and only
  // then write the NEW token. This guarantees the magic link always wins.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mlt = params.get('mlt');
    const legacyTok = params.get('token');
    if (!mlt && !legacyTok) return;

    const wipePriorSession = async () => {
      try {
        localStorage.removeItem('konnect_access_token');
        localStorage.removeItem('konnect_refresh_token');
      } catch { /* private mode */ }
      delete axios.defaults.headers.common['Authorization'];
      // Best-effort service-worker cache purge — older versions cached
      // /api/auth/me responses which leaked the prior user's identity.
      try {
        if (navigator.serviceWorker?.controller) {
          navigator.serviceWorker.controller.postMessage('KONNECT_SW_PURGE');
        }
        if ('caches' in window) {
          const names = await caches.keys();
          await Promise.all(names.map(n => caches.delete(n)));
        }
      } catch { /* ignore */ }
    };

    (async () => {
      await wipePriorSession();
      try {
        if (mlt) {
          const { data } = await axios.post(
            `${API}/api/auth/consume-magic-link`,
            { token: mlt },
            { headers: { Authorization: '' } }, // explicit: no inherited auth header
          );
          if (data?.token) {
            localStorage.setItem('konnect_access_token', data.token);
            if (data.refresh_token) localStorage.setItem('konnect_refresh_token', data.refresh_token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${data.token}`;
            window.location.replace(data.onboarding_completed ? '/dashboard' : '/onboarding');
            return;
          }
        }
        if (legacyTok) {
          localStorage.setItem('konnect_access_token', legacyTok);
          axios.defaults.headers.common['Authorization'] = `Bearer ${legacyTok}`;
          const { data } = await axios.get(`${API}/api/auth/me`);
          if (data?.email) {
            window.location.replace(data.onboarding_completed ? '/dashboard' : '/onboarding');
            return;
          }
        }
        setError('This sign-in link is invalid or has expired. Please request a new one.');
      } catch (err) {
        setError(err?.response?.data?.detail || 'This sign-in link is invalid or has expired. Please request a new one.');
      }
    })();
  }, []);

  const primary = brand?.primary_color || '#10B981';
  const secondary = brand?.secondary_color || null;
  const heading = brand?.login_heading || 'Welcome back';
  const subheading = brand?.login_subheading || 'Sign in to your account to continue';
  const companyName = brand?.company_name || 'Konnect';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // Remember that this user prefers password login
      try { localStorage.setItem('konnect_login_mode', 'password'); } catch { /* ignore */ }
      if (!result.data.onboarding_completed) {
        navigate('/onboarding');
      } else {
        navigate('/dashboard');
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  // Email a one-click sign-in link to the user.
  const [magicSending, setMagicSending] = useState(false);

  const handleMagicLink = async (e) => {
    if (e) e.preventDefault();
    setError('');
    if (!email || !email.includes('@')) {
      setError('Enter your email above to receive a sign-in link.');
      return;
    }
    setMagicSending(true);
    try {
      await axios.post(`${API}/api/auth/magic-link`, { email });
    } catch {
      // We deliberately don't reveal whether the email is registered.
    } finally {
      setMagicSending(false);
      setMagicSent(true);
      try { localStorage.setItem('konnect_login_mode', 'magic'); } catch { /* ignore */ }
    }
  };

  // Background style: solid secondary if branded, else default emerald wash
  const pageStyle = secondary
    ? { background: `linear-gradient(135deg, ${secondary} 0%, ${secondary}f0 100%)` }
    : undefined;

  // Anti-flash guard for whitelabel domains: if we're on what looks like a
  // whitelabel host (not the primary konnect.kaharian.com or localhost) and
  // the brand hasn't resolved yet, show a neutral loading state so the
  // reseller's client never sees the "Konnect" branding briefly. Returning
  // visitors bypass this because localStorage returns brand synchronously.
  const _host = typeof window !== 'undefined' ? window.location.hostname : '';
  const _looksWhitelabel = !!_host && !/^(localhost|127\.|konnect\.kaharian\.com|.*\.preview\.emergentagent\.com)$/i.test(_host);
  if (_looksWhitelabel && !brandResolved && !brand) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-off-white" data-testid="login-brand-loading">
        <div className="w-8 h-8 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen flex items-center justify-center p-4 relative ${secondary ? '' : 'bg-off-white'}`}
      style={pageStyle}
      data-testid="login-page-root"
    >
      <BackButton fallbackHref="/" testId="login-back-btn" />
      <Card className="w-full max-w-md shadow-2xl" data-testid="login-form">
        <CardHeader className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-2 mb-4" data-testid="login-brand-logo">
            {brand?.logo_url ? (
              <img src={brand.logo_url} alt={companyName} className="h-12 w-auto object-contain max-w-[180px]" />
            ) : (
              <img src="/konnect-logo.png" alt="Konnect" className="h-10 w-auto object-contain" />
            )}
          </Link>
          <CardTitle className="text-2xl font-heading" data-testid="login-heading">{heading}</CardTitle>
          <CardDescription data-testid="login-subheading">{subheading}</CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <Alert variant="destructive" className="mb-4" data-testid="login-error">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {backendState === 'warming' && !error && (
            <Alert className="mb-4 border-amber-200 bg-amber-50" data-testid="backend-warming-banner">
              <AlertDescription className="flex items-center gap-2 text-amber-900 text-sm">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Server is warming up — you can still sign in, the first request may take a few seconds.</span>
              </AlertDescription>
            </Alert>
          )}

          {magicSent ? (
            <div className="text-center py-4 space-y-4" data-testid="magic-link-sent">
              <div className="text-5xl">📬</div>
              <div>
                <h3 className="text-lg font-semibold mb-1">Check your inbox</h3>
                <p className="text-sm text-gray-medium">
                  If <strong>{email}</strong> has an account, a sign-in link is on its way.
                  Click it from any device and you&apos;re in.
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setMagicSent(false); setError(''); }}
                className="text-xs text-gray-400 hover:text-gray-600"
                data-testid="magic-link-back"
              >
                ← Use a different email
              </button>
            </div>
          ) : (
            <form onSubmit={mode === 'password' ? handleSubmit : handleMagicLink} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  data-testid="login-email-input"
                  className="focus-ring"
                />
              </div>

              {mode === 'password' && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="password">Password</Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm hover:opacity-80"
                      style={{ color: primary }}
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required={mode === 'password'}
                      autoComplete="current-password"
                      data-testid="login-password-input"
                      className="focus-ring pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                className="w-full text-white hover:opacity-90 transition-opacity"
                style={{ backgroundColor: primary }}
                disabled={loading || magicSending}
                data-testid="login-submit-btn"
              >
                {mode === 'password' ? (
                  loading ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Signing in…</>
                  ) : ('Sign In')
                ) : (
                  magicSending ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending link…</>
                  ) : (
                    <><Mail className="mr-2 h-4 w-4" />Email me a sign-in link</>
                  )
                )}
              </Button>

              <div className="text-center pt-1">
                <button
                  type="button"
                  onClick={() => { setMode(mode === 'password' ? 'magic' : 'password'); setError(''); }}
                  className="text-xs text-gray-500 hover:opacity-80"
                  style={{ color: primary }}
                  data-testid="login-mode-toggle"
                >
                  {mode === 'password' ? '← Use a sign-in link instead' : 'Use a password instead →'}
                </button>
              </div>

              {/* Single small disclosure for the rare cases where direct */}
              {/* login fails (autofill quirks, stuck SW). */}
              <div className="pt-1 text-center">
                <button
                  type="button"
                  onClick={() => setHelpOpen((v) => !v)}
                  className="text-[11px] text-gray-400 hover:text-gray-600 transition-colors"
                  data-testid="login-help-toggle"
                >
                  {helpOpen ? 'Hide help' : 'Need help signing in?'}
                </button>
                {helpOpen && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
                          navigator.serviceWorker.controller.postMessage('KONNECT_SW_PURGE');
                        }
                        if ('serviceWorker' in navigator) {
                          const regs = await navigator.serviceWorker.getRegistrations();
                          await Promise.all(regs.map((r) => r.unregister()));
                        }
                        if ('caches' in window) {
                          const names = await caches.keys();
                          await Promise.all(names.map((n) => caches.delete(n)));
                        }
                        try { localStorage.clear(); sessionStorage.clear(); } catch { /* ignore */ }
                        toast.success('Cleared. Reloading…');
                        setTimeout(() => window.location.reload(true), 500);
                      } catch {
                        toast.error('Could not fully reset. Try Ctrl+Shift+R.');
                      }
                    }}
                    className="mt-2 w-full text-[11px] text-gray-400 hover:text-gray-600 inline-flex items-center justify-center gap-1.5 transition-colors py-2"
                    data-testid="reset-browser-cache-btn"
                  >
                    <RefreshCw className="h-3 w-3" />
                    Page looks stuck? Reset browser cache
                  </button>
                )}
              </div>
            </form>
          )}

          <div className="mt-6 text-center text-sm text-gray-medium">
            Don&apos;t have an account?{' '}
            <Link
              to="/signup"
              className="font-medium hover:opacity-80"
              style={{ color: primary }}
              data-testid="signup-link"
            >
              Sign up
            </Link>
          </div>

          {brand && (
            <div className="mt-6 text-center text-[10px] text-gray-400 space-y-0.5" data-testid="login-brand-footer">
              {brand.footer_text && <div>{brand.footer_text}</div>}
              {!brand.hide_powered_by && <div>Powered by Konnect</div>}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default LoginPage;
